import type { StokAnalizReady, StokRow } from "@/api/stok";
import type { TrKey } from "@/i18n";

/**
 * /stok kategori sekmeleri — sozlesme §2: kritik/yakin/cok satan/az satan/
 * beden/seviye + eski arayuzle esitlik icin "tumu" (legacy stok.html
 * data-cat="tumu", en sonda).
 */
export type StokTabKey = "kritik" | "yakin" | "cok_satan" | "az_satan" | "beden" | "seviye" | "tumu";

export const STOK_TABS: ReadonlyArray<{ key: StokTabKey; labelKey: TrKey }> = [
  { key: "kritik", labelKey: "stok.tab.kritik" },
  { key: "yakin", labelKey: "stok.tab.yakin" },
  { key: "cok_satan", labelKey: "stok.tab.cok_satan" },
  { key: "az_satan", labelKey: "stok.tab.az_satan" },
  { key: "beden", labelKey: "stok.tab.beden" },
  { key: "seviye", labelKey: "stok.tab.seviye" },
  { key: "tumu", labelKey: "stok.tab.tumu" },
];

/**
 * "kritik" sekmesi tukendi (stok<=0) + kritik (kalan_gun<=7) satirlarini
 * BIRLESTIRIR — ozet.kritik_sayisi bu ikisinin toplamidir (bkz.
 * app/core/analytics/stok.py stok_analiz ozet hesaplamasi), tablo bu sayiyla
 * tutarli olmalidir.
 *
 * "tumu" TUM gruplari (tukendi+kritik+yakin+cok_satan+normal+az_satan)
 * BIRLESTIRIR — eski `legacy/js/stok.js` `currentRows()` cat==='tumu' dalinin
 * AYNISI (siralama: gruplarin bu SIRAYLA concat edilmesi; ek client-side
 * siralama YOK, kullanici sutun basligina tiklayip kendisi siralar).
 */
export function urunRowsForTab(data: StokAnalizReady, tab: Exclude<StokTabKey, "beden" | "seviye">): StokRow[] {
  switch (tab) {
    case "kritik":
      return [...data.kategoriler.tukendi, ...data.kategoriler.kritik];
    case "yakin":
      return data.kategoriler.yakin;
    case "cok_satan":
      return data.kategoriler.cok_satan;
    case "az_satan":
      return data.kategoriler.az_satan;
    case "tumu": {
      const k = data.kategoriler;
      return [...k.tukendi, ...k.kritik, ...k.yakin, ...k.cok_satan, ...k.normal, ...k.az_satan];
    }
    default:
      return [];
  }
}

/** Ad/SKU'ya gore basit istemci-tarafi arama (bos sorguda tum satirlar). */
export function filterUrunRows(rows: StokRow[], query: string): StokRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => (r.urun_adi || "").toLowerCase().includes(q) || (r.sku || "").toLowerCase().includes(q));
}

/**
 * SortableTable icin React `rowKey` uretici — DUZELTME TURU (inceleme bulgusu).
 *
 * `urun_kart_id`+`sku` TEK BASINA TEKIL DEGIL: ayni urunun farkli bedenleri
 * ayni sku'yu PAYLASABILIYOR (canli Ticimax deseni) — bkz.
 * backend/tests/core/test_ticimax_adapter.py
 * ::test_sync_products_native_id_disambiguates_on_sku_collision_across_sizes,
 * ve core.product_variants.sku UNIQUE DEGIL, yalniz (tenant_id,
 * platform_native_id) tekil (backend/db/init/100_core_schema.sql:336,348).
 * `beden` eklemek COGU durumu cozer ama sadece renkle ayrisan varyantlarda
 * `beden`/`ek_secenek` de ayni degeri tasiyabilir (app/core/analytics/stok.py
 * `_parse_row`) — backend HENUZ tekil `platform_native_id`'yi satira eklemiyor.
 *
 * Kalici cozum backend'den gelmeli; bu yuzden anahtar VERILEN DIZIDEKI
 * pozisyona (nesne kimligine) gore tekillestirilir: iki satir ayni
 * urun_kart_id+sku+beden'e sahip olsa bile, dizideki FARKLI nesne referanslari
 * oldugu icin farkli anahtar alirlar. SortableTable satirlari yeniden
 * siralarken bile nesne referansi degismedigi icin (yalniz dizi sirasi
 * degisir), bu anahtar o satir icin STABIL kalir — sekme/arama/siralama
 * degisikliklerinde React'in yanlis satiri eslestirip icerik karistirmasi
 * (duplicate key) boylece onlenir.
 */
export function stokRowKeyer(rows: StokRow[]): (row: StokRow) => string {
  const base = (row: StokRow) => `${row.urun_kart_id ?? ""}-${row.sku ?? ""}-${row.beden ?? ""}`;
  const keys = new Map<StokRow, string>();
  rows.forEach((row, index) => {
    keys.set(row, `${base(row)}-${index}`);
  });
  // Map'te yoksa (rowKey, rows'a verilmemis bir nesneyle cagrilmis olabilir —
  // beklenmez ama savunmaci) index'siz tabana duser.
  return (row) => keys.get(row) ?? base(row);
}
